import { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { DollarSign, Coins, Star, Globe, ChevronDown, ChevronUp, Bookmark, MessageCircle, AlertTriangle, Crown, Handshake } from "lucide-react";
import SolicitarIntermedioDialog from "@/components/SolicitarIntermedioDialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import ShareMenu from "@/components/ShareMenu";
import { isNotified } from "@/lib/notified-sold";
import { toast } from "@/hooks/use-toast";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import ContactOptionsPopover from "@/components/ContactOptionsPopover";
import OwnerActionButtons from "@/components/OwnerActionButtons";
import SoldRibbon from "@/components/SoldRibbon";
import { checkIsOwner } from "@/lib/ownership";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import WeaponProficiencyTable from "@/components/WeaponProficiencyTable";
import rubiniCoinsIcon from "@/assets/rubini_coins.gif";
import crystalCoinIcon from "@/assets/crystal_coin.gif";

interface ItemDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequireLogin?: (action: () => void) => void;
  sessionContato?: string;
  sessionToken?: string;
  sessionPerfilId?: string | null;
  ownedAnuncioIds?: Set<string>;
  onAnuncioAction?: () => void;
  item: {
    anuncioId?: string;
    itemNome: string;
    tipo: "compra" | "venda";
    preco: number;
    moeda: string;
    precoCoins?: number | null;
    precoKk?: number | null;
    servidor: string | null;
    destaque: boolean;
    level?: number | null;
    attack?: number | null;
    defense?: number | null;
    defensemod?: number | null;
    primarytype?: string | null;
    weight?: number | null;
    slot?: string | null;
    vocation?: string | null;
    descricao?: string | null;
    hands?: string | null;
    weapontype?: string | null;
    objectclass?: string | null;
    elementalbond?: string | null;
    marketable?: boolean | null;
    pickupable?: boolean | null;
    value?: number | null;
    upgradeclass?: number | null;
    imbueslots?: number | null;
    npcprice?: string | null;
    npcvalue?: string | null;
    notes?: string | null;
    buyfrom?: string | null;
    sellto?: string | null;
    implemented?: string | null;
    stackable?: boolean | null;
    walkable?: boolean | null;
    immobile?: boolean | null;
    history?: string | null;
    usable?: boolean | null;
    droppedby?: any[] | null;
    imageUrl?: string | null;
    range?: string | null;
    atk_mod?: string | null;
    hit_mod?: string | null;
    attrib?: string | null;
    resist?: string | null;
    tier?: number | null;
    earth_attack?: number | null;
    energy_attack?: number | null;
    death_attack?: number | null;
    fire_attack?: number | null;
    ice_attack?: number | null;
    holy_attack?: number | null;
    temWhatsapp?: boolean;
    temEmail?: boolean;
    expired?: boolean;
    expiraEm?: string | null;
    anuncianteId?: string | null;
    anuncianteAvatarUrl?: string | null;
    preferenciasContato?: string[];
    apelido?: string | null;
    anuncioStatus?: string;
    renovacaoPendente?: boolean;
    isIntermediating?: boolean;
  } | null;
}

const getFavorites = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("rpgcoin_favorites") || "[]");
  } catch {
    return [];
  }
};

const toggleFavorite = (id: string): boolean => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx >= 0) {
    favs.splice(idx, 1);
    localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
    return false;
  }
  favs.push(id);
  localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
  return true;
};

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

const getMaxTier = (upgradeclass: number | null | undefined): number => {
  switch (upgradeclass) {
    case 4: return 10;
    case 3: return 8;
    case 2: return 5;
    case 1: return 3;
    default: return 0;
  }
};

const buildStatsLine = (item: NonNullable<ItemDetailModalProps["item"]>): string | null => {
  const parts: string[] = [];
  if (item.range) parts.push(`Range: ${item.range}`);

  const elements = [
    { val: item.energy_attack, label: "energy" },
    { val: item.fire_attack, label: "fire" },
    { val: item.ice_attack, label: "ice" },
    { val: item.earth_attack, label: "earth" },
    { val: item.death_attack, label: "death" },
    { val: item.holy_attack, label: "holy" },
  ];
  const elemental = elements.find(e => e.val != null && e.val > 0);

  if (item.atk_mod) {
    parts.push(`Atk: ${item.atk_mod}`);
  } else if (item.attack != null || elemental) {
    const baseAtk = item.attack ?? 0;
    if (elemental) {
      parts.push(`Atk: ${baseAtk} + ${elemental.val} ${elemental.label}`);
    } else {
      parts.push(`Atk: ${baseAtk}`);
    }
  }

  if (item.defense != null || item.defensemod != null) {
    const baseDef = item.defense ?? 0;
    if (item.defensemod != null) {
      const sign = item.defensemod >= 0 ? `+${item.defensemod}` : `${item.defensemod}`;
      parts.push(`Def: ${baseDef} ${sign}`);
    } else {
      parts.push(`Def: ${baseDef}`);
    }
  }

  if (item.hit_mod) parts.push(`Hit%: ${item.hit_mod}`);
  if (item.attrib) parts.push(item.attrib);
  if (item.resist) parts.push(`protection ${item.resist}`);
  return parts.length > 0 ? `(${parts.join(", ")}).` : null;
};

const getHandsText = (hands: string | null | undefined): string | null => {
  if (!hands) return null;
  const lower = hands.toLowerCase();
  if (lower === "one" || lower === "one-handed" || lower === "1") return "Arma de uma mão";
  if (lower === "two" || lower === "two-handed" || lower === "2") return "Arma de duas mãos";
  return hands;
};

const ItemDetailModal = ({ open, onOpenChange, onRequireLogin, sessionContato, sessionToken, sessionPerfilId, ownedAnuncioIds, onAnuncioAction, item }: ItemDetailModalProps) => {
  const [isFav, setIsFav] = useState(() => item?.anuncioId ? getFavorites().includes(item.anuncioId) : false);
  const [reportOpen, setReportOpen] = useState(false);
  const [isReported, setIsReported] = useState(() => item?.anuncioId ? isNotified(item.anuncioId) : false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [attribOpen, setAttribOpen] = useState(true);
  const [intermedioOpen, setIntermedioOpen] = useState(false);
  const { data: siteConfig } = useConfiguracoes();

  const statsLine = useMemo(() => item ? buildStatsLine(item) : null, [item]);
  const maxTier = useMemo(() => item ? getMaxTier(item.upgradeclass) : 0, [item?.upgradeclass]);
  const handsText = useMemo(() => item ? getHandsText(item.hands) : null, [item?.hands]);
  const attrRows = useMemo(() => {
    if (!item) return [];
    const rows: { icon: string; label: string; value: string }[] = [];
    if (item.objectclass) rows.push({ icon: "📦", label: "Classification", value: item.objectclass });
    if (item.slot) rows.push({ icon: "🦺", label: "Slot", value: item.slot });
    const ht = getHandsText(item.hands);
    if (ht) rows.push({ icon: "✋", label: "Tipo", value: ht });
    if (item.weapontype) rows.push({ icon: "⚔️", label: "Arma", value: item.weapontype });
    if (item.tier != null && item.tier > 0) rows.push({ icon: "💎", label: "Tier", value: String(item.tier) });
    if (item.imbueslots != null && item.imbueslots > 0) rows.push({ icon: "🔮", label: "Imbue Slots", value: String(item.imbueslots) });
    if (item.elementalbond) rows.push({ icon: "🌀", label: "Elemento", value: item.elementalbond });
    return rows;
  }, [item?.objectclass, item?.slot, item?.hands, item?.weapontype, item?.tier, item?.imbueslots, item?.elementalbond]);

  if (!item) return null;

  const anuncioId = item.anuncioId;

  const isOwner = checkIsOwner(
    sessionContato ? { contato: sessionContato, perfil_id: sessionPerfilId || null } : null,
    { anunciante_id: item.anuncianteId, id: item.anuncioId },
    ownedAnuncioIds,
  );

  const isSold = item.anuncioStatus === "vendido";
  const isCancelled = item.anuncioStatus === "encerrado";

  const handleFavorite = () => {
    if (!anuncioId) return;
    const doFav = () => {
      const added = toggleFavorite(anuncioId);
      setIsFav(added);
      toast({ title: added ? "Adicionado à lista!" : "Removido da lista" });
    };
    if (onRequireLogin) {
      onRequireLogin(doFav);
    } else {
      doFav();
    }
  };

  const handleReportClick = () => {
    const doOpen = () => setReportOpen(true);
    if (onRequireLogin) {
      onRequireLogin(doOpen);
    } else {
      doOpen();
    }
  };

  const hasAnyPrice = (item.preco > 0) || (item.precoCoins != null && item.precoCoins > 0) || (item.precoKk != null && item.precoKk > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tibia-border bg-card p-0 max-h-[90vh] flex flex-col max-w-sm sm:max-w-2xl md:max-w-3xl [&]:fixed [&>button.absolute]:top-2 [&>button.absolute]:right-2">
        <DialogHeader className="sr-only">
          <DialogTitle>{item.itemNome}</DialogTitle>
          <DialogDescription>Detalhes do item {item.itemNome}</DialogDescription>
        </DialogHeader>

        {isSold && <SoldRibbon />}
        {isCancelled && <SoldRibbon type="cancelado" />}

        {isOwner && (
          <div className="absolute top-2 left-2 z-20 flex items-center gap-0.5 bg-primary/90 text-primary-foreground px-1.5 py-0.5 rounded-sm">
            <Crown className="w-3 h-3" />
            <span className="font-pixel text-[6px] tracking-wider">MEU</span>
          </div>
        )}

        {/* Top banner */}
        <div className={`px-4 py-2 text-center font-pixel text-[8px] tracking-wider flex items-center justify-center gap-1 flex-shrink-0 ${
          item.tipo === "venda"
            ? "bg-green-600/30 text-green-400"
            : "bg-blue-600/30 text-blue-400"
        }`}>
          {item.tipo === "venda" ? (
            <><DollarSign className="w-3 h-3" /> ITEM À VENDA</>
          ) : (
            <><Coins className="w-3 h-3" /> PROCURANDO ITEM</>
          )}
          {item.destaque && <Star className="w-3 h-3 text-primary fill-primary ml-2" />}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-2">
          {/* Top section: image+server | wiki info | prices */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 mb-3">
            {/* Left: Image with tier badge + Server below */}
            <div className="flex-shrink-0 flex flex-col items-center justify-start gap-1.5">
              <div className="relative tibia-border-inner bg-secondary/20 p-2 rounded-sm w-24 h-24 flex items-center justify-center">
                <img
                  src={getItemImageUrl(item.itemNome)}
                  alt={item.itemNome}
                  className="w-20 h-20 object-contain image-rendering-pixelated"
                  onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
                  loading="lazy"
                />
                {item.tier != null && item.tier > 0 && (
                  <div className="absolute -top-2 -left-2 z-20">
                    <div className="w-7 h-7 rounded-full bg-stone-900 border-2 border-primary flex items-center justify-center shadow-md">
                      <span className="text-primary font-bold text-xs leading-none">{item.tier}</span>
                    </div>
                  </div>
                )}
              </div>
              {item.servidor && (
                <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground tibia-border-inner bg-secondary/20 px-2 py-0.5 rounded-sm w-24">
                  <Globe className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{item.servidor}</span>
                </div>
              )}
            </div>


            {/* Middle: Wiki info */}
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-foreground leading-tight">{item.itemNome}</h3>

              {statsLine && (
                <p className="text-xs text-green-500 font-medium mt-0.5">{statsLine}</p>
              )}

              {(item.vocation || item.level != null) && (
                <p className="text-[11px] text-foreground mt-1 italic leading-snug">
                  It can only be wielded properly by{" "}
                  <span className="font-semibold">{item.vocation || "all vocations"}</span>
                  {item.level != null && (
                    <> of level <span className="font-semibold">{item.level}</span> or higher</>
                  )}
                  .
                </p>
              )}

              {item.imbueslots != null && item.imbueslots > 0 && (
                <p className="text-[11px] text-foreground mt-0.5">
                  Imbuement Slots: <span className="font-semibold">{item.imbueslots}</span>.
                </p>
              )}

              {item.upgradeclass != null && item.upgradeclass > 0 && (
                <p className="text-[11px] text-foreground mt-0.5">
                  Classification: <span className="font-semibold">{item.upgradeclass}</span>.
                  {maxTier > 0 && (
                    <> Max. Tier: <span className="font-semibold">{maxTier}</span></>
                  )}
                </p>
              )}

              {item.weight != null && (
                <p className="text-[11px] text-foreground mt-0.5">
                  It weighs <span className="font-semibold">{item.weight.toFixed(2)} oz</span>.
                </p>
              )}
            </div>

            {/* Right (desktop) / Bottom (mobile): Prices */}
            {hasAnyPrice && (
              <div className="order-last sm:order-none tibia-border-inner bg-secondary/20 rounded-sm p-2 w-full sm:w-auto sm:flex-shrink-0 sm:min-w-[130px] sm:self-start">
                <div className="flex flex-row sm:flex-col justify-around sm:justify-start sm:space-y-1 gap-2 sm:gap-0">
                  {item.precoCoins != null && item.precoCoins > 0 && (
                    <div className="flex items-center justify-center sm:justify-between gap-2">
                      <img src={rubiniCoinsIcon} alt="RC" className="w-5 h-5" />
                      <span className="font-pixel text-[9px] text-primary gold-text-glow">
                        {item.precoCoins.toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}
                  {item.preco > 0 && (
                    <div className="flex items-center justify-center sm:justify-between gap-2">
                      <span className="font-pixel text-[12px] text-green-400 font-bold drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]">R$</span>
                      <span className="font-pixel text-[9px] text-primary gold-text-glow">
                        {item.preco.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {item.precoKk != null && item.precoKk > 0 && (
                    <div className="flex items-center justify-center sm:justify-between gap-2">
                      <img src={crystalCoinIcon} alt="KK" className="w-5 h-5" />
                      <span className="font-pixel text-[9px] text-amber-400 gold-text-glow">
                        {item.precoKk.toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>


          {/* Diamond separator */}
          <div className="relative flex items-center justify-center my-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-primary/30" />
            </div>
            <div className="relative z-10 bg-card px-2">
              <div className="w-3 h-3 rotate-45 bg-primary/60 border border-primary/80" />
            </div>
          </div>

          {/* Attributes section - in columns */}
          <div className="mb-3">
            <button
              onClick={() => setAttribOpen(!attribOpen)}
              className="flex items-center gap-1 font-pixel text-[7px] tracking-wider text-muted-foreground hover:text-foreground transition-colors mb-1"
            >
              <span>▸</span>
              <span>ATRIBUTOS</span>
              {attribOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {attribOpen && attrRows.length > 0 && (
              <div className="tibia-border-inner bg-secondary/20 rounded-sm p-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-1.5">
                {attrRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[11px] min-w-0">
                    <span className="flex-shrink-0">{row.icon}</span>
                    <span className="text-muted-foreground flex-shrink-0">{row.label}:</span>
                    <span className="text-foreground font-medium truncate">{row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          {item.descricao && (
            <>
              <div className="flex items-center gap-1.5 font-pixel text-[7px] tracking-wider text-muted-foreground mb-1">
                <span>📝</span>
                <span>DESCRIÇÃO</span>
              </div>
              <div className="tibia-border-inner bg-secondary/20 rounded-sm p-3">
                <p className="text-xs text-foreground whitespace-pre-wrap break-all">{item.descricao}</p>
              </div>
            </>
          )}

          {item.itemNome && (
            <WeaponProficiencyTable itemName={item.itemNome} />
          )}
        </div>

        {/* Termo do item */}
        {siteConfig?.termo_item?.trim() && (
          <div className="tibia-border-inner bg-secondary/20 rounded-sm p-2 mx-4 mb-2 max-h-20 overflow-y-auto flex-shrink-0">
            <p className="text-[8px] text-muted-foreground whitespace-pre-wrap leading-tight">
              {siteConfig.termo_item}
            </p>
          </div>
        )}

        {/* Fixed action buttons at bottom */}
        {anuncioId && (
          isOwner ? (
            <div className="flex-shrink-0">
              <OwnerActionButtons
                anuncioId={anuncioId}
                anuncioStatus={item.anuncioStatus}
                variant="card"
                expiraEm={item.expiraEm || null}
                isDestaque={item.destaque}
                destaqueInicio={(item as any).destaqueInicio || null}
                sessionContato={sessionContato}
                sessionToken={sessionToken}
                actionLoading={actionLoading}
                setActionLoading={setActionLoading}
                onAnuncioAction={() => { onAnuncioAction?.(); onOpenChange(false); }}
                onAnuncioRefresh={onAnuncioAction}
                renovacaoPendente={item.renovacaoPendente}
                itemNome={item.itemNome}
              />
            </div>
          ) : !item.expired ? (
            <div className="flex-shrink-0 border-t border-border/40 flex items-center justify-between w-full py-3">
              <div className="flex-1 flex justify-center">
                <ContactOptionsPopover
                  anuncioId={anuncioId!}
                  itemNome={item.itemNome}
                  temWhatsapp={item.temWhatsapp}
                  temEmail={item.temEmail}
                  anuncianteId={item.anuncianteId}
                  anuncianteApelido={item.apelido}
                  anuncianteAvatarUrl={item.anuncianteAvatarUrl}
                  preferenciasContato={item.preferenciasContato || ["whatsapp"]}
                  onRequireLogin={onRequireLogin}
                  sessionContato={sessionContato}
                  sessionToken={sessionToken}
                >
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button className="h-9 w-9 flex items-center justify-center hover:scale-110 transition-all">
                        <MessageCircle className="w-4 h-4 text-green-500/70 hover:text-green-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="z-[100]">Contatar</TooltipContent>
                  </Tooltip>
                </ContactOptionsPopover>
              </div>

              {!isSold && !item.isIntermediating && (
                <div className="flex-1 flex justify-center">
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          const doOpen = () => setIntermedioOpen(true);
                          if (onRequireLogin) { onRequireLogin(doOpen); } else { doOpen(); }
                        }}
                        className="h-9 w-9 flex items-center justify-center hover:scale-110 transition-all"
                      >
                        <Handshake className="w-4 h-4 text-purple-400/70 hover:text-purple-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="z-[100]">Solicitar Intermédio</TooltipContent>
                  </Tooltip>
                </div>
              )}

              <div className="flex-1 flex justify-center">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button onClick={handleFavorite} className="h-9 w-9 flex items-center justify-center hover:scale-110 transition-all">
                      <Bookmark className={`w-4 h-4 ${isFav ? "fill-primary text-primary" : "text-amber-400/70 hover:text-amber-400"}`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="z-[100]">Adicionar à lista</TooltipContent>
                </Tooltip>
              </div>

              <div className="flex-1 flex justify-center">
                <ShareMenu
                  url={`${window.location.origin}/item/${anuncioId}`}
                  text={`Confira "${item.itemNome}" no RPG Coin!`}
                  buttonClassName="h-9 w-9 flex items-center justify-center hover:scale-110 transition-all"
                  iconClassName="w-4 h-4 text-blue-400/70 hover:text-blue-400"
                />
              </div>

              {!isReported && (
                <div className="flex-1 flex justify-center">
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button onClick={handleReportClick} className="h-9 w-9 flex items-center justify-center hover:scale-110 transition-all">
                        <AlertTriangle className="w-4 h-4 text-red-400/70 hover:text-red-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="z-[100]">Reportar problema</TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          ) : null
        )}

        {/* Report Problem Dialog */}
        {anuncioId && (
          <ReportProblemDialog
            open={reportOpen}
            onOpenChange={setReportOpen}
            anuncioId={anuncioId}
            itemNome={item.itemNome}
            tier={item.tier ?? item.upgradeclass}
            servidor={item.servidor}
            onReported={() => setIsReported(true)}
          />
        )}

        {/* Solicitar Intermédio Dialog */}
        {anuncioId && sessionContato && sessionToken && (
          <SolicitarIntermedioDialog
            open={intermedioOpen}
            onOpenChange={setIntermedioOpen}
            anuncioId={anuncioId}
            itemNome={item.itemNome}
            preco={item.preco}
            moeda={item.moeda}
            precoCoins={item.precoCoins}
            precoKk={item.precoKk}
            servidor={item.servidor}
            descricao={item.descricao}
            anuncianteApelido={item.apelido}
            anuncianteAvatarUrl={item.anuncianteAvatarUrl}
            itemImageUrl={item.imageUrl || getItemImageUrl(item.itemNome)}
            sessionContato={sessionContato}
            sessionToken={sessionToken}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ItemDetailModal;
